import React, { useState, useEffect, lazy, Suspense } from "react";
import { Box, Typography, TextField, Button, useMediaQuery, useTheme } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { batchDetails } from "../../../constants/External";

// Code-split the consultant popup so its JS isn't in the hero's critical path —
// it's only needed after the user clicks "Apply Now".
const TalkToOurConsultantPopup = lazy(() => import("../Popup/TalkToOurConsultantPopupV3"));

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const formatDate = (d) => `${String(d.getDate()).padStart(2, "0")} ${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;

// batchDetails[1] looks like "06 Jul - 02 Jan 2027" — end date carries the only explicit year,
// so the start year is inferred by checking whether the batch wraps into the next calendar year.
const [startPart, endPart] = batchDetails[1].split(" - ");
const [startDay, startMonthStr] = startPart.split(" ");
const [, endMonthStr, endYearStr] = endPart.split(" ");
const startMonthIndex = MONTH_NAMES.indexOf(startMonthStr);
const endMonthIndex = MONTH_NAMES.indexOf(endMonthStr);
const endYear = parseInt(endYearStr, 10);
const startYear = startMonthIndex > endMonthIndex ? endYear - 1 : endYear;

const batchStartDate = new Date(startYear, startMonthIndex, parseInt(startDay, 10));

const NEXT_BATCH_START = formatDate(batchStartDate);
const ADMISSION_CLOSE_DATE = formatDate(new Date());

const heroBullets = [
  <><strong>35 Guaranteed Job Opportunities</strong></>,
  <>₹5–45 LPA Package Opportunities</>,
  <>Microsoft & AWS Certification Vouchers Included</>,
  <>Career Fee Refund if No Job Opportunities Are Provided <Box component="span" sx={{ fontSize: 11.5, color: "#888" }}>(subject to applicable terms and conditions)</Box></>,
  <>7,640 Candidates Placed</>,
  <>50–150% Salary Hike Potential</>,
];

const inlineProof = [
  "🔴 210+ applied this month",
  "⚡ Only 3 seats in next batch",
  "🏅 Ranked #1 by Deccan Herald",
];

export default function HeroSectionTihIit({ city }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [ttocPopupOpen, setTtocPopupOpen] = useState(false);
  // Only mount the popup (and fetch its chunk) once it has actually been opened.
  const [ttocPopupMounted, setTtocPopupMounted] = useState(false);

  // Once the hero scrolls out of view (same trigger the sticky header uses),
  // morph the inline "Guaranteed Job Opportunity" badge into a fixed, rotated
  // ribbon — pinned to the left edge on desktop, just below the sticky header on mobile.
  const [isBadgeDocked, setIsBadgeDocked] = useState(false);
  useEffect(() => {
    const hero = document.getElementById("hero");
    if (!hero) return;

    const observer = new IntersectionObserver(
      ([entry]) => setIsBadgeDocked(!entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(hero);
    return () => observer.disconnect();
  }, []);

  const openTtocPopup = () => {
    setTtocPopupMounted(true);
    setTtocPopupOpen(true);
  };

  const [gclid] = useState(() => {
    const urlGclid = new URLSearchParams(window.location.search).get("gclid");
    if (urlGclid) {
      localStorage.setItem("gclid", urlGclid);
      return urlGclid;
    }
    return localStorage.getItem("gclid") || "";
  });

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });
  const [errors, setErrors] = useState({});

  const validateFirstName = (v) => (!v ? "First name is required" : "");
  const validateLastName = (v) => (!v ? "Last name is required" : "");
  const validateEmail = (v) => {
    if (!v) return "Email is required";
    const r = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return r.test(v) ? "" : "Invalid email address";
  };
  const validatePhone = (v) => {
    if (!v) return "Phone is required";
    const r = /^\d{10}$/;
    return r.test(v) ? "" : "Phone must be 10 digits";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // map Zoho names → state fields
    const map = {
      "First Name": "firstName",
      "Last Name": "lastName",
      Email: "email",
      Phone: "phone",
    };

    setFormData({
      ...formData,
      [map[name]]: value,
    });
  };

  const handleSubmit = (e) => {
    const f = validateFirstName(formData.firstName);
    const l = validateLastName(formData.lastName);
    const em = validateEmail(formData.email);
    const p = validatePhone(formData.phone);

    if (f || l || em || p) {
      e.preventDefault();
      setErrors({ firstName: f, lastName: l, email: em, phone: p });
      return;
    }

    // allow browser submit to Zoho (no manual submit)
  };

  return (
    <>
      <Box
        sx={{
          width: "100vw",
          position: "relative",
          left: "50%",
          right: "50%",
          marginLeft: "-50vw",
          marginRight: "-50vw",
        }}
      >
        <motion.div
          animate={{ scale: [1, 1.02, 1] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "relative",
            overflow: "hidden",
            background: "linear-gradient(90deg, #ff3d00, #ff8a00, #ffb300, #ff8a00, #ff3d00)",
            boxShadow: "0 6px 20px rgba(255, 106, 0, 0.35)",
          }}
        >
          <motion.div
            initial={{ x: "-120%" }}
            animate={{ x: "220%" }}
            transition={{ duration: 2.2, repeat: Infinity, ease: "linear" }}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "35%",
              height: "100%",
              background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.45), transparent)",
              pointerEvents: "none",
            }}
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: { xs: 0.8, md: 1.2 },
              flexWrap: "wrap",
              py: { xs: 1.2, md: 1.4 },
              px: { xs: 2, md: 3 },
              textAlign: "center",
            }}
          >
            <Typography
              component="span"
              sx={{
                fontSize: { xs: 15, md: 18 },
                fontWeight: 800,
                color: "#fff",
                fontFamily: "Poppins, sans-serif",
                letterSpacing: "0.2px",
                textShadow: "0 1px 6px rgba(0,0,0,0.2)",
              }}
            >
              🪔 Diwali Sale — Flat ₹30,000 Discount 🪔
            </Typography>
            <Typography
              component="span"
              sx={{
                fontSize: { xs: 10.5, md: 12 },
                fontWeight: 700,
                color: "#7a1a00",
                background: "#fff",
                borderRadius: "999px",
                px: 1.2,
                py: 0.3,
                fontFamily: "Poppins, sans-serif",
                letterSpacing: "0.4px",
                textTransform: "uppercase",
                whiteSpace: "nowrap",
              }}
            >
              Limited Time
            </Typography>
          </Box>
        </motion.div>
      </Box>
      <AnimatePresence>
      {isBadgeDocked && (
        <motion.div
          layoutId="guaranteed-job-badge"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, rotate: isMobile ? 0 : -90 }}
          exit={{ opacity: 0 }}
          transition={{ type: "spring", stiffness: 260, damping: 26 }}
          style={{
            position: "fixed",
            top: isMobile ? 58 : "42%",
            left: isMobile ? 12 : -60,
            transformOrigin: "left top",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            background: isMobile ? "white" : "rgba(255,107,53,0.12)",
            border: "1px solid rgba(255,107,53,0.4)",
            borderRadius: 20,
            padding: "6px 16px",
            boxShadow: "0 6px 18px rgba(0,0,0,0.15)",
            zIndex: 1200,
            whiteSpace: "nowrap",
            cursor: "pointer",
          }}
          onClick={openTtocPopup}
        >
          <Typography
            sx={{
              fontSize: 12.5,
              fontWeight: 700,
              letterSpacing: "0.3px",
              color: "#e05a28",
              fontFamily: "Poppins, sans-serif",
              whiteSpace: "nowrap",
            }}
          >
            Guaranteed Job Opportunity
          </Typography>
        </motion.div>
      )}
    </AnimatePresence>
      <Box
        sx={{
          background: "linear-gradient(135deg, #f4e9ff 0%, #ece2fb 50%, #e8f0fd 100%)",
          py: { xs: 4, md: 6 },
          px: { xs: 2, md: "5%" },
        }}
      >
        <Box
          sx={{
            maxWidth: 1200,
            mx: "auto",
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 420px" },
            gap: { xs: 4, md: 6 },
            alignItems: "start",
          }}
        >
          {/* LEFT COPY */}
          <Box>

            <Box sx={{ mb: 1.5, minHeight: { xs: 0, md: 34 } }}>
              <AnimatePresence>
                {!isBadgeDocked && (
                  <motion.div
                    layoutId="guaranteed-job-badge"
                    transition={{ type: "spring", stiffness: 260, damping: 26 }}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      background: "rgba(255,107,53,0.12)",
                      border: "1px solid rgba(255,107,53,0.4)",
                      borderRadius: 20,
                      padding: "6px 16px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 12.5,
                        fontWeight: 700,
                        letterSpacing: "0.3px",
                        color: "#e05a28",
                        fontFamily: "Poppins, sans-serif",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Guaranteed Job Opportunity
                    </Typography>
                  </motion.div>
                )}
              </AnimatePresence>
            </Box>
            <Typography
              component="h1"
              sx={{
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: "1.5px",
                textTransform: "uppercase",
                color: "#8C00FF",
                mb: 1.2,
                fontFamily: "Poppins, sans-serif",
              }}
            >
              DevOps Course Online with Placement Support*
            </Typography>

            {city ?
              <Typography
                component="h1"
                sx={{
                  fontSize: { xs: "1.5rem", sm: "2rem", md: "2.2rem" },
                  fontWeight: 800,
                  lineHeight: 1.2,
                  color: "#1a1a2e",
                  mb: 1.5,
                  fontFamily: "Poppins, sans-serif",
                }}
              >
                Become an{" "}
                <Box component="span" sx={{ color: "#8C00FF" }}>
                  AI-Driven DevOps &amp; Cloud Engineer
                </Box>{" "}
                in 6 Months in {city}
              </Typography>
              :
              <Typography
                component="h1"
                sx={{
                  fontSize: { xs: "1.6rem", sm: "2rem", md: "2.5rem" },
                  fontWeight: 800,
                  lineHeight: 1.2,
                  color: "#1a1a2e",
                  mb: 1.5,
                  fontFamily: "Poppins, sans-serif",
                }}
              >
                Become an{" "}
                <Box component="span" sx={{ color: "#8C00FF" }}>
                  AI-Driven DevOps &amp; Cloud Engineer
                </Box>{" "}
                in 6 Months
              </Typography>}

            <Box
              sx={{
                fontSize: "1rem",
                fontWeight: 600,
                color: "#8C00FF",
                mb: 2.2,
                py: 1,
                px: 2,
                background: "rgba(140,0,255,0.08)",
                borderLeft: "4px solid #8C00FF",
                borderRadius: "0 8px 8px 0",
                display: "inline-block",
                fontFamily: "Poppins, sans-serif",
              }}
            >
              Online DevOps Course · Multi-Cloud (AWS, Azure, GCP) · Gen AI — Career Ready Program
            </Box>

            <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, mb: 2 }}>
              {heroBullets.map((bullet, i) => (
                <Box
                  component="li"
                  key={i}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    fontSize: 14.5,
                    color: "#444",
                    mb: 1.1,
                    lineHeight: 1.5,
                    fontFamily: "Poppins, sans-serif",
                  }}
                >
                  <Box component="span" sx={{ color: "#22c55e", fontWeight: 700, mt: "1px" }}>
                    ✓
                  </Box>
                  <span>{bullet}</span>
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                background: "linear-gradient(135deg, #8C00FF, #6d00cc)",
                color: "#fff",
                borderRadius: "12px",
                p: { xs: 2, md: "16px 24px" },
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
                mb: 2.5,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap" }}>
                <Box>
                  <Typography
                    sx={{
                      fontSize: 11.5,
                      fontWeight: 600,
                      opacity: 0.82,
                      textTransform: "uppercase",
                      letterSpacing: "0.4px",
                      whiteSpace: "nowrap",
                      fontFamily: "Poppins, sans-serif",
                    }}
                  >
                    🔴 Live Classes Starting
                  </Typography>
                  <Typography sx={{ fontSize: 17, fontWeight: 800, fontFamily: "Poppins, sans-serif" }}>
                    {NEXT_BATCH_START}
                  </Typography>
                </Box>
                <Box sx={{ width: "1px", height: 38, background: "rgba(255,255,255,0.3)", display: { xs: "none", sm: "block" } }} />
                <Box>
                  <Typography
                    sx={{
                      fontSize: 11.5,
                      fontWeight: 600,
                      opacity: 0.82,
                      textTransform: "uppercase",
                      letterSpacing: "0.4px",
                      whiteSpace: "nowrap",
                      fontFamily: "Poppins, sans-serif",
                    }}
                  >
                    Admission Closes
                  </Typography>
                  <Typography sx={{ fontSize: 17, fontWeight: 800, fontFamily: "Poppins, sans-serif" }}>
                    {ADMISSION_CLOSE_DATE}
                  </Typography>
                </Box>
              </Box>
              <Button
                aria-label="Apply Now"
                onClick={openTtocPopup}
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  background: "#ff6b35",
                  color: "#fff",
                  py: 1.2,
                  px: 3,
                  borderRadius: "10px",
                  fontWeight: 700,
                  fontSize: 14.5,
                  whiteSpace: "nowrap",
                  textTransform: "none",
                  fontFamily: "Poppins, sans-serif",
                  "&:hover": { background: "#e05a28" },
                }}
              >
                🚀 Apply Now
              </Button>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, flexWrap: "wrap", mb: 2.5 }}>
              {inlineProof.map((pip) => (
                <Box
                  key={pip}
                  sx={{
                    background: "#f4e9ff",
                    border: "1px solid rgba(108,43,217,0.2)",
                    borderRadius: "20px",
                    py: 0.5,
                    px: 1.5,
                    fontWeight: 600,
                    color: "#8C00FF",
                    fontSize: 12,
                    fontFamily: "Poppins, sans-serif",
                  }}
                >
                  {pip}
                </Box>
              ))}
            </Box>

            {/* <Box
            sx={{
              background: "#f4e9ff",
              borderRadius: "12px",
              p: "14px 18px",
              fontSize: 14,
              color: "#444",
              lineHeight: 1.6,
              border: "1px solid rgba(108,43,217,0.15)",
              fontFamily: "Poppins, sans-serif",
            }}
          >
            🏆 Don't just get certified —{" "}
            <Box component="strong" sx={{ color: "#8C00FF" }}>
              get hired with 100% Guaranteed Opportunities
            </Box>
            . No opportunity secured? Full career-fee refunded.
          </Box> */}
          </Box>

          {/* RIGHT FORM */}
          <Box
            sx={{
              background: "#fff",
              borderRadius: "20px",
              boxShadow: "0 8px 40px rgba(108,43,217,0.18)",
              p: "26px 24px",
              border: "2px solid rgba(108,43,217,0.1)",
              position: { xs: "static", md: "sticky" },
              top: 24,
            }}
          >
            <Box sx={{ textAlign: "center", mb: 1.8 }}>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#1a1a2e", mb: 0.3, fontFamily: "Poppins, sans-serif" }}>
                Get Curriculum
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: "#666", fontFamily: "Poppins, sans-serif" }}>
                Fill in your details — our advisor will call you right away
              </Typography>
            </Box>

            <Box
              component="form"
              id="webform774394000000340007"
              name="WebToLeads774394000000340007"
              action="https://crm.zoho.in/crm/WebToLeadForm"
              method="POST"
              acceptCharset="UTF-8"
              onSubmit={handleSubmit}
            >
              <input type="hidden" name="xnQsjsdp" defaultValue="40ece394749f91d79a4c954e678b8efa8f026b2f827541e1bcaec32d82f61516" />
              <input type="hidden" name="xmIwtLD" defaultValue="cb437a0dd19d47c4156b5f4df83832c6a3264439df6470d80fc6fca2fdd81bc54f55c83d957ac993ebc769821b75609f" />
              <input type="hidden" name="actionType" defaultValue="TGVhZHM=" />
              <input type="hidden" name="returnURL" defaultValue="https://paperlive.in/thankyou" />
              <input type="hidden" id="zc_gad" name="zc_gad" value={gclid} />
              <input type="hidden" name="Website" value={window.location.href.slice(0, 250)} />
              <input type="hidden" name="Description" value="DevOps Course Online - Hero Form" />

              <Box sx={{ display: "flex", gap: 1.5, mb: 1.5 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 600, mb: 0.5, color: "#444", fontFamily: "Poppins, sans-serif" }}>
                    First Name
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Rahul"
                    id="First_Name"
                    name="First Name"
                    value={formData.firstName}
                    onChange={handleChange}
                    error={!!errors.firstName}
                    size="small"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", fontFamily: "Poppins" } }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 600, mb: 0.5, color: "#444", fontFamily: "Poppins, sans-serif" }}>
                    Last Name
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Sharma"
                    id="Last_Name"
                    name="Last Name"
                    value={formData.lastName}
                    onChange={handleChange}
                    error={!!errors.lastName}
                    size="small"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", fontFamily: "Poppins" } }}
                  />
                </Box>
              </Box>

              <Typography sx={{ fontSize: 12.5, fontWeight: 600, mb: 0.5, color: "#444", fontFamily: "Poppins, sans-serif" }}>
                Email
              </Typography>
              <TextField
                fullWidth
                placeholder="you@email.com"
                type="email"
                id="Email"
                name="Email"
                value={formData.email}
                onChange={handleChange}
                error={!!errors.email}
                size="small"
                sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { borderRadius: "8px", fontFamily: "Poppins" } }}
              />

              <Typography sx={{ fontSize: 12.5, fontWeight: 600, mb: 0.5, color: "#444", fontFamily: "Poppins, sans-serif" }}>
                Phone Number
              </Typography>
              <TextField
                fullWidth
                placeholder="9876543210"
                type="tel"
                id="Phone"
                name="Phone"
                value={formData.phone}
                onChange={handleChange}
                error={!!errors.phone}
                size="small"
                sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: "8px", fontFamily: "Poppins" } }}
              />

              <Button
                aria-label="Get Curriculum"
                type="submit"
                fullWidth
                sx={{
                  background: "#ff6b35",
                  color: "#fff",
                  py: 1.4,
                  borderRadius: "10px",
                  fontSize: 15,
                  fontWeight: 700,
                  textTransform: "none",
                  fontFamily: "Poppins, sans-serif",
                  "&:hover": { background: "#e05a28" },
                }}
              >
                Get Curriculum
              </Button>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.7, mt: 1, fontSize: 11.5, color: "#666", fontFamily: "Poppins, sans-serif" }}>
              🔒 Your information is 100% secure and will not be shared.
            </Box>
          </Box>
        </Box>

        {ttocPopupMounted && (
          <Suspense fallback={null}>
            <TalkToOurConsultantPopup
              open={ttocPopupOpen}
              onClose={() => setTtocPopupOpen(false)}
            />
          </Suspense>
        )}
      </Box>
    </>
  );
}
